import "./styles.scss";

import React, { Component, Fragment, useState } from "react";
import { useParams } from "react-router-dom";
import Icon from "@/components/ui/Icon.jsx";

import Card from "@/components/ui/Card";
import ChatBotSourcesWebsite from "@/pages/chatbot/sources/ChatBotSourcesWebsite";
import ChatBotSourcesFiles from "@/pages/chatbot/sources/ChatBotSourcesFiles.tsx";
import ChatBotSourcesText from "@/pages/chatbot/sources/ChatBotSourcesText";
import ChatBotSourcesQuestion from "@/pages/chatbot/sources/ChatBotSourcesQuestion";
import { useQuery, useQueryClient } from "react-query";
import { constants } from "@/constants.js";
import { urlChatID } from "@/utils/url.js";
import { toast } from "react-toastify";
import { queryKey } from "@/store/queryKeys.ts";
import {funcToastifyHandler} from "@/store/api";
import {useTranslation} from "react-i18next";
import {Badge} from "@/components/shadcn/badge.tsx";

export const queryKeyChatbotSummary = "chatbot.summary";

const apiRetrain = async () => {
  await funcToastifyHandler(
      () => {return fetch(`${constants.url.API_PREFIX}chatbot/${urlChatID()}/retrain`)},
      "Train Error"
  )
};

let prevProgress = null;
let checkStatusTimer = null;
const ChatBotSources = (props) => {
  const {t} = useTranslation();
  const queryClient = useQueryClient();

  const [cardOpened, setCardOpened] = useState(true);

  let statusQuery = useQuery(queryKey.chatbotSummary, async () => {
    let res = await fetch(
      `${constants.url.API_PREFIX}chatbot/${urlChatID()}/status`,
    );
    return res.json();
  });

  const refetcher = async function () {
    return statusQuery.refetch().then((res) => {
      if (res.data) {
        startWatcher();
        if (res.data.training_error) {
          toast.error(res.data.training_error, {
            position: "top-right",
            autoClose: 3000,
            theme: "light",
          });
        }
        if (prevProgress === true && !res.data.training) {
          toast.success("Chatbot retraining has finished", {
            position: "top-right",
            autoClose: 1500,
            theme: "light",
          });
        }
        !res.data.training && clearInterval(checkStatusTimer);
        prevProgress = res.data.training;

        queryClient.invalidateQueries(queryKey.chatbotSourcesTexts());
        queryClient.invalidateQueries(queryKey.chatbotSourcesFiles());
        queryClient.invalidateQueries(queryKey.chatbotSourcesLinks());
        queryClient.invalidateQueries(queryKey.chatbotSourcesQuestions());
      }
    });
  };
  const startWatcher = (force) => {
    if (
      force ||
      (statusQuery.data && statusQuery.data.training && !checkStatusTimer)
    ) {
      checkStatusTimer = setInterval(refetcher, 4000);
    }
  };
  startWatcher();

  const trainBtnRenderer = () => {
    const {t} = useTranslation();
    return (
      <button
        type="submit"
        className="btn btn inline-flex justify-center btn-primary btn"
        onClick={async () => {
          await apiRetrain();
          await startWatcher(true);
          await refetcher();
        }}
      >
        <span className="flex items-center">
          {statusQuery.data && statusQuery.data.training && (
            <Icon
              className="animate-spin mr-2"
              icon="fluent:arrow-clockwise-20-regular"
              color="#bbb"
              width="25"
              height="25"
            />
          )}
          {!statusQuery.data ||
            (!statusQuery.data.training && (
              <Icon
                className="mr-2"
                icon="fluent:brain-circuit-24-regular"
                color="#bbb"
                width="25"
                height="25"
              />
            ))}

          <span className="ml-2">{t("knowledge_base.retrain_chatbot")}</span>
        </span>
      </button>
    );
  };

  const docSumRender = () => {
    let { isLoading, isSuccess, data } = statusQuery;
    let docs = isSuccess ? data.docs : null;
    return (
      <div>
        {isLoading && <div></div>}
        {!isLoading && isSuccess && docs && (
          <div>
            <div>
              {data.chatbot.trained_at
                ? `${t("knowledge_base.chatbot_trained")}: ${new Date(
                    data.chatbot.trained_at * 1000,
                  ).toLocaleString()}`
                : <Badge>{t("knowledge_base.chatbot_not_trained")}</Badge>}
            </div>

            <table className="min-w-full text-left text-sm font-light">
              <thead className="border-b bg-white font-medium dark:border-neutral-500 dark:bg-neutral-600">
                <tr>
                  <th scope="col" className="px-6 py-4">
                    {t("knowledge_base.content")}
                  </th>
                  <th scope="col" className="px-6 py-4">
                    {t("knowledge_base.trained")}
                  </th>
                  <th scope="col" className="px-6 py-4">
                    {t("knowledge_base.new")}
                  </th>
                  <th scope="col" className="px-6 py-4">
                    {t("knowledge_base.limit")}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b bg-slate-50">
                  <td className="whitespace-nowrap px-6 py-2 font-medium">
                    {t("knowledge_base.web_pages")}
                  </td>
                  <td className="whitespace-nowrap px-6 py-2">{docs.links}</td>
                  <td className="whitespace-nowrap px-6 py-2">
                    {docs.links_new ? `+${docs.links_new}` : ""}
                  </td>
                  <td className="whitespace-nowrap px-6 py-2">{data.web_pages_limit}</td>
                </tr>
                <tr className="border-b">
                  <td className="whitespace-nowrap px-6 py-2 font-medium">
                    {t("knowledge_base.files")}
                  </td>
                  <td className="whitespace-nowrap px-6 py-2">{docs.files}</td>
                  <td className="whitespace-nowrap px-6 py-2">
                    {docs.files_new ? `+${docs.files_new}` : ""}
                  </td>
                  <td className="whitespace-nowrap px-6 py-2">{data.files_limit}</td>
                </tr>
                <tr className="border-b bg-slate-50">
                  <td className="whitespace-nowrap px-6 py-2 font-medium">
                    {t("knowledge_base.texts")}
                  </td>
                  <td className="whitespace-nowrap px-6 py-2">{docs.texts}</td>
                  <td className="whitespace-nowrap px-6 py-2">
                    {docs.texts_new ? `+${docs.texts_new}` : ""}
                  </td>
                  <td className="whitespace-nowrap px-6 py-2"></td>
                </tr>
                <tr className="border-b">
                  <td className="whitespace-nowrap px-6 py-2 font-medium">
                    {t("knowledge_base.qa")}
                  </td>
                  <td className="whitespace-nowrap px-6 py-2">
                    {docs.questions}
                  </td>
                  <td className="whitespace-nowrap px-6 py-2">
                    {docs.questions_new ? `+${docs.questions_new}` : ""}
                  </td>
                  <td className="whitespace-nowrap px-6 py-2"></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  let renderedSource = null;
  let title = null;
  const { source } = useParams();
  switch (source) {
    case "links":
      title = t("sidebar.web_pages");
      renderedSource = <ChatBotSourcesWebsite />;
      break;
    case "files":
      title = t("sidebar.files");
      renderedSource = <ChatBotSourcesFiles />;
      break;
    case "text":
      title = t("sidebar.text");
      renderedSource = <ChatBotSourcesText />;
      break;
    case "question":
      title = t("sidebar.qa");
      renderedSource = <ChatBotSourcesQuestion />;
      break;
  }

  return (
    <div
      className={
        "flex flex-col-reverse max-w-[1900px] w-full " +
        (cardOpened ? "2xl:justify-end 2xl:flex-row" : "")
      }
    >
      <Card
        title={title ? title : "Data Sources"}
        className={"mr-4 w-full"}
      >
        {renderedSource && renderedSource}
      </Card>
      <Card
        title={
          <div className="flex justify-between items-center">
            <div
                className="cursor-pointer"
                onClick={() => {setCardOpened((state) => {return !state;})}}
            >
              {t("knowledge_base.status")}
            </div>
            <div className="flex items-center justify-between">
              {!cardOpened &&
                  <a type="button" className="btn inline-flex justify-center btn-outline-primary btn mr-2" href="/pricing">
                    <span className="flex items-center cursor-pointer">{t("billing.upgrade_plan")}</span>
                  </a>
              }
              {!cardOpened && trainBtnRenderer()}

              <div
                className="ml-6 opacity-70 hover:opacity-40 cursor-pointer"
                onClick={() => {
                  setCardOpened((state) => {
                    return !state;
                  });
                }}
              >
                {cardOpened && (
                  <Icon
                    icon="fluent:chevron-up-20-regular"
                    width="30"
                    height="30"
                  />
                )}
                {!cardOpened && (
                  <Icon
                    icon="fluent:chevron-down-20-regular"
                    width="30"
                    height="30"
                  />
                )}
              </div>
            </div>
          </div>
        }
        // titleClass="w-full"
        className={"mb-4 " + (cardOpened ? "2xl:mb-0" : "")}
        bodyClass={cardOpened ? "p-6" : ""}
      >
        {cardOpened && (
          <div>
            {docSumRender()}
            {/*{progressBarRenderer()}*/}
            <div className="flex justify-between mt-4">
              <a type="button" className="btn btn inline-flex justify-center btn-outline-primary btn" href="/pricing">
                <span className="flex items-center cursor-pointer">{t("billing.upgrade_plan")}</span>
              </a>
              {trainBtnRenderer()}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ChatBotSources;
